// src/services/llmStreamingService.ts
import { TradeRecommendation } from '../providers/tradingTypes';
import { getHatateToken } from '../helpers/utils';

const API_BASE_URL = 'https://backend.anubisai.net/api';
const LLM_ENDPOINT = `${API_BASE_URL}/bot/llm`;

/**
 * Solicita una recomendación de trading al LLM con soporte para streaming
 * @param params Parámetros de la solicitud
 * @param onUpdate Callback para actualizaciones parciales
 * @param onComplete Callback para respuesta completa
 */
export const getTradingRecommendationStream = async (
  params: {
    symbol: string;
    timeframe: string;
    marketData?: any[];
    riskLevel?: string;
  },
  onUpdate: (text: string) => void,
  onComplete: (recommendation: TradeRecommendation) => void
) => {
  try {
    // Preparar prompt para el LLM
    const prompt = buildTradingPrompt(params);
    
    // Iniciar streaming
    requestLLMStream(
      prompt,
      {
        symbol: params.symbol,
        timeframe: params.timeframe,
        dataType: 'tradingRecommendation',
        riskLevel: params.riskLevel || 'medium'
      },
      // Callback para chunks parciales
      (chunk) => {
        onUpdate(chunk);
      },
      // Callback para respuesta completa
      (result) => {
        const recommendation = processRecommendationResult(result, params.symbol);
        onComplete(recommendation);
      }
    );
  } catch (error) {
    console.error('Error al solicitar recomendación en streaming:', error);
    // Crear recomendación por defecto en caso de error
    onComplete(createDefaultRecommendation(params.symbol, params.marketData));
  }
};

/**
 * Solicita un análisis de mercado al LLM con soporte para streaming
 */
export const getMarketAnalysisStream = async (
  params: {
    symbol: string;
    timeframe: string;
    marketData?: any[];
  },
  onUpdate: (text: string) => void,
  onComplete: (analysis: string) => void
) => {
  try {
    // Construir prompt para análisis de mercado
    const prompt = `Analiza el mercado actual de ${params.symbol} en timeframe ${params.timeframe}. 
    Proporciona un análisis técnico detallado que incluya:
    1. Tendencia principal
    2. Niveles de soporte y resistencia
    3. Indicadores técnicos relevantes (RSI, medias móviles, MACD, etc.)
    4. Patrones de velas destacables
    5. Volumen y su relación con el precio
    6. Perspectivas a corto, medio y largo plazo`;
    
    // Iniciar streaming
    requestLLMStream(
      prompt,
      {
        symbol: params.symbol,
        timeframe: params.timeframe,
        dataType: 'marketAnalysis'
      },
      // Callback para chunks parciales
      (chunk) => {
        onUpdate(chunk);
      },
      // Callback para respuesta completa
      (result) => {
        if (result.text) {
          onComplete(result.text);
        } else if (result.response) {
          onComplete(result.response);
        } else {
          onComplete("No se pudo generar un análisis completo.");
        }
      }
    );
  } catch (error) {
    console.error('Error al solicitar análisis en streaming:', error);
    onComplete("Error al generar el análisis de mercado. Por favor, intenta nuevamente.");
  }
};

/**
 * Construye un prompt específico para trading
 */
const buildTradingPrompt = (params: any): string => {
  // Preparar datos de mercado resumidos si están disponibles
  let marketSummary = '';
  if (params.marketData && params.marketData.length > 0) {
    const lastPrice = params.marketData[params.marketData.length - 1].price;
    const firstPrice = params.marketData[0].price;
    const changePercent = ((lastPrice - firstPrice) / firstPrice) * 100;
    
    // Extraer máximos y mínimos
    const prices = params.marketData.map((p: any) => p.price);
    const high = Math.max(...prices);
    const low = Math.min(...prices);
    
    // Calcular medias móviles simples
    const ma7 = calculateMA(prices, 7);
    const ma25 = calculateMA(prices, 25);
    
    // Calcular RSI simplificado
    const rsi = calculateSimpleRSI(prices);
    
    marketSummary = `
      Precio actual: ${lastPrice}
      Cambio: ${changePercent.toFixed(2)}%
      Máximo: ${high}
      Mínimo: ${low}
      Media móvil 7 periodos: ${ma7.toFixed(2)}
      Media móvil 25 periodos: ${ma25.toFixed(2)}
      RSI: ${rsi.toFixed(2)}
    `;
  }
  
  const riskLevelText = params.riskLevel ? 
    `\nConsiderando un nivel de riesgo ${params.riskLevel} (bajo/medio/alto).` : '';
  
  // Construir prompt completo
  return `Analiza el mercado de ${params.symbol} en timeframe ${params.timeframe} y proporciona una recomendación de trading detallada.
  
  ${marketSummary ? `DATOS DE MERCADO:\n${marketSummary}` : ''}
  ${riskLevelText}
  
  Por favor, proporciona tu recomendación con exactamente el siguiente formato:
  1. ACCIÓN: [BUY/SELL/HOLD]
  2. PRECIO DE ENTRADA: [precio sugerido]
  3. STOP LOSS: [nivel de stop loss]
  4. TAKE PROFIT: [nivel de take profit]
  5. CONFIANZA: [nivel de confianza del 0-100]
  6. RAZÓN: [explicación clara y concisa de la recomendación]
  7. INDICADORES CLAVE: [lista de indicadores relevantes]
  
  Asegúrate de que tu recomendación sea clara, específica y basada en el análisis técnico del mercado actual.`;
};

/**
 * Función principal para consultar al LLM con soporte para streaming
 */
const requestLLMStream = async (
  contextualPrompt: string,
  additionalContext: any,
  onUpdate: (text: string) => void,
  onComplete: (response: any) => void
) => {
  try {
    const apiToken = getHatateToken();
    const timestamp = new Date().toISOString();
    
    const response = await fetch(LLM_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `${apiToken}`
      },
      body: JSON.stringify({
        prompt: [{
          content: contextualPrompt,
          role: 'user',
          time: timestamp
        }],
        chatId: `trading_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        skill: 'ambipar_v1',
        module: 'ambipar_hazmat_tech',
        // Contexto adicional para trading
        contextData: additionalContext
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Error en la solicitud: ${response.status}`);
    }
    
    if (!response.body) {
      throw new Error('No se recibió stream');
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulatedText = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      const chunk = decoder.decode(value, { stream: true });
      accumulatedText += chunk;
      
      // Notificar sobre cada actualización
      onUpdate(chunk);
    }
    
    // Procesar el texto completo
    let result;
    try {
      // Intentar parsear como JSON
      result = JSON.parse(accumulatedText);
    } catch (e) {
      // Si no es JSON, devolver como texto
      result = { 
        success: true, 
        text: accumulatedText,
        timestamp: new Date().toISOString()
      };
    }
    
    // Notificar completado
    onComplete(result);
    
  } catch (error) {
    console.error('Error en streaming de LLM:', error);
    onComplete({ 
      success: false, 
      message: 'Error en la comunicación con el servicio de IA',
      timestamp: new Date().toISOString()
    });
  }
};

/**
 * Procesa el resultado de la respuesta del LLM
 */
const processRecommendationResult = (result: any, symbol: string): TradeRecommendation => {
  // Si tenemos una estructura JSON clara
  if (result.recommendation) {
    return {
      id: `rec-${Date.now()}`,
      symbol,
      action: result.recommendation.action || 'HOLD',
      price: parseFloat(result.recommendation.price || 0),
      stopLoss: parseFloat(result.recommendation.stopLoss || 0),
      takeProfit: parseFloat(result.recommendation.takeProfit || 0),
      confidence: parseInt(result.recommendation.confidence || 50),
      reason: result.recommendation.reason || 'Análisis basado en condiciones actuales',
      indicators: result.recommendation.indicators || [],
      timestamp: new Date().toISOString()
    };
  }
  
  // Si tenemos texto, extraer la recomendación
  if (result.text || result.response) {
    const text = result.text || result.response;
    return extractRecommendationFromText(text, symbol);
  }
  
  // Respuesta por defecto
  return {
    id: `rec-${Date.now()}`,
    symbol,
    action: 'HOLD',
    price: 0,
    stopLoss: 0,
    takeProfit: 0,
    confidence: 50,
    reason: 'No se pudo extraer una recomendación clara',
    indicators: [],
    timestamp: new Date().toISOString()
  };
};

/**
 * Extrae una recomendación de trading desde texto
 */
const extractRecommendationFromText = (text: string, symbol: string): TradeRecommendation => {
  // Buscar la acción (BUY/SELL/HOLD)
  const actionRegex = /acci[oóò]n:?\s*(COMPRAR?|VENDER?|MANTENER|BUY|SELL|HOLD)/i;
  const actionMatch = text.match(actionRegex);
  const action = mapAction(actionMatch ? actionMatch[1].toUpperCase() : "HOLD");
  
  // Buscar el precio de entrada
  const priceRegex = /precio\s*(?:de\s*entrada)?:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const priceMatch = text.match(priceRegex);
  const price = priceMatch ? parseFloat(priceMatch[1].replace(',', '.')) : 0;
  
  // Buscar stop loss
  const stopLossRegex = /stop\s*loss:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const stopLossMatch = text.match(stopLossRegex);
  const stopLoss = stopLossMatch ? parseFloat(stopLossMatch[1].replace(',', '.')) : 0;
  
  // Buscar take profit
  const takeProfitRegex = /take\s*profit:?\s*[$]?\s*(\d+[.,]?\d*)/i;
  const takeProfitMatch = text.match(takeProfitRegex);
  const takeProfit = takeProfitMatch ? parseFloat(takeProfitMatch[1].replace(',', '.')) : 0;
  
  // Buscar confianza
  const confidenceRegex = /confianza:?\s*(\d+)(?:\s*%)?/i;
  const confidenceMatch = text.match(confidenceRegex);
  const confidence = confidenceMatch ? parseInt(confidenceMatch[1]) : 50;
  
  // Buscar razón/explicación
  const reasonRegex = /raz[oóò]n:?\s*(.+?)(?:\n\d|\n$|$)/i;
  const reasonMatch = text.match(reasonRegex);
  let reason = reasonMatch ? reasonMatch[1].trim() : "";
  
  // Si no encontramos una razón específica, usar parte del texto
  if (!reason) {
    // Buscar la explicación más larga entre párrafos
    const paragraphs = text.split(/\n+/);
    let longestParagraph = "";
    
    for (const paragraph of paragraphs) {
      if (paragraph.length > longestParagraph.length && 
          !paragraph.includes("ACCIÓN:") && 
          !paragraph.includes("PRECIO") &&
          !paragraph.includes("STOP LOSS") && 
          !paragraph.includes("TAKE PROFIT") &&
          !paragraph.includes("CONFIANZA")) {
        longestParagraph = paragraph;
      }
    }
    
    reason = longestParagraph || "Análisis basado en condiciones actuales del mercado";
  }
  
  // Extraer indicadores
  const indicators: any[] = [];
  
  // Buscar RSI
  const rsiRegex = /RSI\s*(?:\(?\d+\)?)?:?\s*(\d+[.,]?\d*)/i;
  const rsiMatch = text.match(rsiRegex);
  if (rsiMatch) {
    indicators.push({
      name: "RSI",
      value: parseFloat(rsiMatch[1].replace(',', '.'))
    });
  }
  
  // Buscar medias móviles
  const maRegex = /MA\s*(\d+):?\s*(\d+[.,]?\d*)/ig;
  let maMatch;
  while ((maMatch = maRegex.exec(text)) !== null) {
    indicators.push({
      name: `MA${maMatch[1]}`,
      value: parseFloat(maMatch[2].replace(',', '.'))
    });
  }
  
  return {
    id: `rec-${Date.now()}`,
    symbol,
    action,
    price,
    stopLoss,
    takeProfit,
    confidence,
    reason,
    indicators,
    timestamp: new Date().toISOString()
  };
};

/**
 * Crea una recomendación por defecto
 */
const createDefaultRecommendation = (symbol: string, marketData?: any[]): TradeRecommendation => {
  const currentPrice = marketData && marketData.length > 0 
    ? marketData[marketData.length - 1].price 
    : 0;
    
  return {
    id: `rec-error-${Date.now()}`,
    symbol,
    action: 'HOLD',
    price: currentPrice,
    confidence: 50,
    timestamp: new Date().toISOString(),
    reason: 'No se pudo generar una recomendación. Manteniendo posición actual.',
    stopLoss: 0,
    takeProfit: 0,
    indicators: []
  };
};

/**
 * Mapea acciones en español/inglés a formato estándar
 */
const mapAction = (action: string): 'BUY' | 'SELL' | 'HOLD' => {
  const normalizedAction = action.toUpperCase().trim();
  
  if (normalizedAction === 'COMPRAR' || normalizedAction === 'COMPRA' || normalizedAction === 'BUY') {
    return 'BUY';
  }
  
  if (normalizedAction === 'VENDER' || normalizedAction === 'VENTA' || normalizedAction === 'SELL') {
    return 'SELL';
  }
  
  return 'HOLD'; // Valor por defecto
};

/**
 * Calcula una media móvil simple
 */
const calculateMA = (prices: number[], period: number): number => {
  if (prices.length < period) {
    return prices.reduce((sum, price) => sum + price, 0) / prices.length;
  }
  
  const relevantPrices = prices.slice(-period);
  return relevantPrices.reduce((sum, price) => sum + price, 0) / period;
};

/**
 * Calcula un RSI simplificado
 */
const calculateSimpleRSI = (prices: number[]): number => {
  if (prices.length < 3) return 50; // Valor neutral
  
  let gains = 0;
  let losses = 0;
  let count = 0;
  
  // Usar los últimos 14 periodos o menos si no hay suficientes
  const period = Math.min(14, prices.length - 1);
  
  for (let i = prices.length - period; i < prices.length; i++) {
    const change = prices[i] - prices[i - 1];
    if (change >= 0) {
      gains += change;
    } else {
      losses -= change; // Convertir a positivo
    }
    count++;
  }
  
  if (count === 0) return 50;
  
  const avgGain = gains / count;
  const avgLoss = losses / count;
  
  if (avgLoss === 0) return 100; // Evitar división por cero
  
  const rs = avgGain / avgLoss;
  return 100 - (100 / (1 + rs));
};